import { useMemo, useState } from "react";
import { Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import DateRangeFilter from "../../components/dateRangeFilter";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import { useGetBankDepositHistoryQuery } from "../../services/bankDeposit";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface BankDepositRecord {
  key:          string;
  dateTime?:    string;
  branch?:      string;
  amount?:      number;
  remark?:      string;
  receiptImage?: string;
}

interface DateRangeValue { start: Date; end: Date; }

/* ─── Constants ──────────────────────────────────────────────────────── */

const defaultDateRange = (): DateRangeValue => ({
  start: dayjs().subtract(14, 'day').startOf('day').toDate(),
  end:   dayjs().endOf('day').toDate(),
});

const renderAmount = (v?: number) =>
  v === undefined || v === null ? '-' : <span style={{ color: '#52c41a' }}>₹{v.toLocaleString('en-IN')}</span>;

const renderDateTime = (v?: string) => {
  if (!v) return '-';
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return v;
  return (
    <div>
      <div>{dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
      <div style={{ fontSize: 12, opacity: 0.65 }}>{dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────── */

const BankDepositHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const accountNumber = searchParams.get('accountNumber') || undefined;
  const bankName = (location.state as { bankName?: string } | null)?.bankName;

  const [page,      setPage]      = useState(1);
  const [limit,     setLimit]     = useState(10);
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(defaultDateRange);

  const { data: historyResp, isFetching } = useGetBankDepositHistoryQuery(
    {
      accountNumber: accountNumber as string,
      startDate: dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined,
      endDate:   dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : undefined,
      page,
      limit,
    },
    { skip: !accountNumber },
  );

  const apiData = historyResp as any;

  const records = useMemo<BankDepositRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((d: any) => ({
      key:          d._id,
      dateTime:     d.createdAt || d.dateTime,
      branch:       d.branchId?.name || d.branch?.name,
      amount:       d.amount,
      remark:       d.remark,
      receiptImage: d.receiptImage,
    }));
  }, [apiData]);

  const columns = [
    { title: 'Date',    dataIndex: 'dateTime',      key: 'dateTime',      render: renderDateTime },
    { title: 'Branch',  dataIndex: 'branch',        key: 'branch',        render: (v?: string) => v || '-' },
    { title: 'Amount',  dataIndex: 'amount',        key: 'amount',        render: renderAmount },
    { title: 'Remark',  dataIndex: 'remark',        key: 'remark',        render: (v?: string) => v || '-' },
    { title: 'Receipt', dataIndex: 'receiptImage',  key: 'receiptImage',  align: 'center' as const,
      render: (v?: string) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '-' },
  ];

  return (
    <div className="bank-deposit-history-page">
      <div className="header-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
          <h2 style={{ margin: 0 }}>{bankName || 'Bank'} Deposits</h2>
        </div>
        <div className="right-col">
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
          />
        </div>
      </div>

      <div className="bank-deposit-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isFetching}
          pagination={false}
          rowKey={(record: BankDepositRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default BankDepositHistory;
